/** Webenv-specific environment management tools (6 tools). */

import type { UniswapDeployment } from "../../deploy/index.js";
import type { DeploymentMode } from "../../runtime/types.js";
import type { TevmClients } from "../context.js";
import type { ToolResult } from "../types.js";
import type { Address } from "viem";

export interface EnvironmentContext {
  clients: TevmClients;
  getDeployment(): UniswapDeployment | null;
  deploy(
    mode: DeploymentMode,
    forkUrl?: string,
    forkBlock?: bigint,
  ): Promise<UniswapDeployment>;
  reset(): Promise<void>;
  getForkInfo(): {
    rpcUrl?: string;
    chainId: number;
    forkBlock?: bigint;
  } | null;
}

function toToolResult(data: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(data, null, 2) }] };
}

function toErrorResult(message: string): ToolResult {
  return {
    content: [{ type: "text", text: JSON.stringify({ error: message }) }],
    isError: true,
  };
}

/** Collect all addresses from a UniswapDeployment into a flat map. */
function collectAddresses(
  deployment: UniswapDeployment,
): Record<string, Address> {
  const result: Record<string, Address> = {};
  const phases = [
    "foundation",
    "v2",
    "v3",
    "v4",
    "universalRouter",
    "uniswapX",
    "erc8004",
  ] as const;
  for (const phase of phases) {
    const addrs = deployment[phase];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (addrs && typeof addrs === "object") {
      for (const [key, value] of Object.entries(addrs)) {
        if (typeof value === "string" && value.startsWith("0x")) {
          result[`${phase}.${key}`] = value as Address;
        }
      }
    }
  }
  return result;
}

export async function deployEnvironmentHandler(
  params: Record<string, unknown>,
  ctx: EnvironmentContext,
): Promise<ToolResult> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const mode = (params.mode as DeploymentMode) ?? "from-scratch";
  const forkUrl = params.forkUrl as string | undefined;
  const forkBlock = params.forkBlock
    ? BigInt(params.forkBlock as string)
    : undefined;

  try {
    const deployment = await ctx.deploy(mode, forkUrl, forkBlock);
    const addresses = collectAddresses(deployment);
    return toToolResult({
      status: "deployed",
      mode,
      chainId: deployment.chainId,
      contractCount: Object.keys(addresses).length,
    });
  } catch (err) {
    return toErrorResult(
      `Deployment failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export function getEnvironmentHandler(
  _params: Record<string, unknown>,
  ctx: EnvironmentContext,
): ToolResult {
  const deployment = ctx.getDeployment();
  if (!deployment) {
    return toToolResult({
      status: "not_deployed",
      message: "No environment deployed yet",
    });
  }
  return toToolResult({
    status: "deployed",
    chainId: deployment.chainId,
    deployer: deployment.deployer,
    blockNumber: deployment.blockNumber,
    addresses: collectAddresses(deployment),
  });
}

export function discoverContractsHandler(
  _params: Record<string, unknown>,
  ctx: EnvironmentContext,
): ToolResult {
  const deployment = ctx.getDeployment();
  if (!deployment) {
    return toErrorResult("No environment deployed — deploy first");
  }
  const addresses = collectAddresses(deployment);
  return toToolResult({
    discovered: Object.keys(addresses).length,
    addresses,
  });
}

export async function resetEnvironmentHandler(
  params: Record<string, unknown>,
  ctx: EnvironmentContext,
): Promise<ToolResult> {
  if (!params.confirm) {
    return toErrorResult(
      "Confirmation required: set confirm=true to reset the environment",
    );
  }
  try {
    await ctx.reset();
    return toToolResult({
      status: "reset",
      message: "Environment cleared and ready for redeployment",
    });
  } catch (err) {
    return toErrorResult(
      `Reset failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export function getForkInfoHandler(
  _params: Record<string, unknown>,
  ctx: EnvironmentContext,
): ToolResult {
  const info = ctx.getForkInfo();
  if (!info) {
    return toToolResult({
      forked: false,
      message: "Running in from-scratch mode (no fork)",
    });
  }
  return toToolResult({
    forked: true,
    rpcUrl: info.rpcUrl,
    chainId: info.chainId,
    forkBlock: info.forkBlock?.toString(),
  });
}

export async function resetForkHandler(
  _params: Record<string, unknown>,
  ctx: EnvironmentContext,
): Promise<ToolResult> {
  const info = ctx.getForkInfo();
  if (!info) {
    return toErrorResult("Not in fork mode — cannot reset fork");
  }
  try {
    await ctx.reset();
    return toToolResult({ status: "fork_reset", chainId: info.chainId });
  } catch (err) {
    return toErrorResult(
      `Fork reset failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** All environment tool definitions for registration. */
export const ENVIRONMENT_TOOLS = [
  {
    name: "deploy_environment",
    description: "Deploy the Uniswap environment (from scratch or forked)",
    handler: deployEnvironmentHandler,
  },
  {
    name: "get_environment",
    description:
      "Get current deployment info (mode, chain ID, contract addresses)",
    handler: getEnvironmentHandler,
  },
  {
    name: "discover_contracts",
    description: "Discover deployed contracts at canonical addresses",
    handler: discoverContractsHandler,
  },
  {
    name: "reset_environment",
    description: "Reset the environment (requires confirm=true)",
    handler: resetEnvironmentHandler,
  },
  {
    name: "get_fork_info",
    description: "Get fork metadata (RPC URL, chain ID, fork block)",
    handler: getForkInfoHandler,
  },
  {
    name: "reset_fork",
    description: "Reset a forked environment to its origin state",
    handler: resetForkHandler,
  },
] as const;
